/**
 * SizilPro — useStaticPages
 * TanStack Query hooks for static_pages and proposal_static_pages tables.
 *
 * File: src/modules/proposal-builder/hooks/useStaticPages.ts
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store/authStore";
import type { StaticPage, StaticPageInsert } from "../types";

const staticPagesKey = (clientId: string | null) =>
  ["static-pages", clientId ?? "no-client"] as const;

const proposalDetailKey = (proposalId: string | undefined) =>
  ["proposals", "detail", proposalId ?? "missing"] as const;

// ─── All active static pages for current client ──────────────
export function useStaticPages() {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<StaticPage[]>({
    queryKey: staticPagesKey(clientId),
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from("static_pages")
        .select("*")
        .eq("client_id", clientId)
        .eq("active", true)
        .order("page_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as StaticPage[];
    },
    enabled: !!clientId,
    staleTime: 1000 * 60 * 2,
    refetchOnMount: true,
  });
}

// ─── Create page ──────────────────────────────────────────────
export function useCreateStaticPage() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (page: StaticPageInsert) => {
      if (!clientId) throw new Error("No client ID");

      const { data, error } = await supabase
        .from("static_pages")
        .insert({
          ...page,
          client_id: clientId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as StaticPage;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staticPagesKey(clientId) });
    },
  });
}

// ─── Update page ──────────────────────────────────────────────
export function useUpdateStaticPage() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<StaticPageInsert>;
    }) => {
      if (!clientId) throw new Error("No client ID");

      const { data, error } = await supabase
        .from("static_pages")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as StaticPage;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staticPagesKey(clientId) });
    },
  });
}

// ─── Deactivate page (soft delete) ───────────────────────────
export function useDeactivateStaticPage() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!clientId) throw new Error("No client ID");

      const { data, error } = await supabase
        .from("static_pages")
        .update({
          active: false,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as StaticPage;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staticPagesKey(clientId) });
    },
  });
}

// ─── Manage which static pages are attached to a proposal ─────
export function useProposalStaticPages(proposalId: string | undefined) {
  const qc = useQueryClient();

  const addPage = useMutation({
    mutationFn: async ({
      staticPageId,
      pageOrder,
    }: {
      staticPageId: string;
      pageOrder: number;
    }) => {
      if (!proposalId) throw new Error("No proposal ID");

      const { error } = await supabase.from("proposal_static_pages").insert({
        proposal_id: proposalId,
        static_page_id: staticPageId,
        page_order: pageOrder,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proposalDetailKey(proposalId) });
      qc.invalidateQueries({ queryKey: ["proposals"] });
    },
  });

  const removePage = useMutation({
    mutationFn: async (staticPageId: string) => {
      if (!proposalId) throw new Error("No proposal ID");

      const { error } = await supabase
        .from("proposal_static_pages")
        .delete()
        .eq("proposal_id", proposalId)
        .eq("static_page_id", staticPageId);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proposalDetailKey(proposalId) });
      qc.invalidateQueries({ queryKey: ["proposals"] });
    },
  });

  return { addPage, removePage };
}